/** @jsxImportSource @emotion/react */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useReadLocalStorage } from 'usehooks-ts';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeConstructorOptions } from '@stripe/stripe-js';
import { Fade, Modal, useMediaQuery } from '@mui/material';
import { REACT_APP_STRIPE_KEY } from '../app.config';

import payStyles from './pay.styles';
import StripePaymentForm from './components/StripePaymentForm/stripe-payment-form.component';
import SpinnerLoader from '../../components/spinner-loader/spinner-loader.component';
import MenuBlock from '../../components/menu-block/menu-block';
import { EStationPricingOption } from '../../api/types';
import { AnimationWrapper } from '../set-up/components/AnimationWrapper';
import { PoweredBy } from '../layouts/components/powered-by/powered-by.component';
import { useGetTransactionByIdQuery } from '../../api/transactions/transactions.api';
import { useStationSocket } from '../../hooks/useStationSocket';
import { useSelector } from 'react-redux';
import { selectAppState } from '../../store/app/app.selector';
import { useTransactionHistory } from '../../hooks/useTransactionHistory';
import { Z_INDEX } from '../../constants/zIndex';
import SavedContactsPreview from '../../components/saved-contacts-preview/saved-contacts-preview.component';
import { useRedirectHomeOnNotFound } from '../../hooks/useRedirectHomeOnNotFound';
import { getStationLocationId } from '../../utils/station';
import { isNotFoundError } from '../../utils/api-error';
import { getConnectorDisplayLabel } from '../../utils/connector-identifier';
import { getLetterByIndex } from '../../utils/utils';

const getStripeLocale = (locale: string): StripeConstructorOptions['locale'] => {
    if (locale === 'ua') {
        return 'uk' as StripeConstructorOptions['locale'];
    }
    if (['en', 'es', 'fr'].includes(locale)) {
        return locale as StripeConstructorOptions['locale'];
    }
    return 'en';
};

const Pay: FC = () => {
    const intl = useIntl();
    const isDesktop = useMediaQuery('(min-width:600px)');
    const { locale } = useSelector(selectAppState);
    const navigation = useNavigate();
    const recentLocations = useReadLocalStorage<string[]>('recentLocations');
    const [stripePromise, setStripePromise] = useState<Stripe | null>();
    const { updateRecentTransactionId } = useTransactionHistory();

    const { guestId } = useParams();
    const {
        data: transaction,
        isLoading: transactionLoading,
        error,
    } = useGetTransactionByIdQuery(guestId ?? '', {
        skip: !guestId,
    });

    const { station } = useStationSocket(transaction?.station?._id);

    const displayStation = station?.data ?? transaction?.station;

    useRedirectHomeOnNotFound(error, transactionLoading);

    const handleOpenChargePage = (id: string) => {
        const recent = recentLocations ? recentLocations : [];
        const locationId = getStationLocationId(displayStation);
        if (locationId) {
            recent.push(locationId);
        }
        localStorage.setItem(
            'recentLocations',
            JSON.stringify(
                recent.filter(
                    (value: string, index: number, array: string[]) =>
                        array.indexOf(value) === index
                )
            )
        );
        updateRecentTransactionId(id);
        navigation(`/charge/${id}`);
    };

    const priceTotal = useMemo(() => {
        if (
            transaction &&
            transaction?.pricing &&
            transaction?.pricing?.pricingOption !== EStationPricingOption.free
        ) {
            return `${Number(transaction?.paymentIntent?.amount / 100).toFixed(2)} ${transaction.pricing.currency}`;
        }
        return undefined;
    }, [transaction?.paymentIntent?.amount, transaction?.pricing?.currency]);

    useEffect(() => {
        const fetchStripeDate = async () => {
            let stripePromise;
            const stripeLocale = getStripeLocale(locale);
            if (transaction?.paymentIntent?.stripeAccount) {
                stripePromise = await loadStripe(REACT_APP_STRIPE_KEY, {
                    stripeAccount: transaction.paymentIntent.stripeAccount,
                    locale: stripeLocale,
                });
            } else {
                stripePromise = await loadStripe(REACT_APP_STRIPE_KEY, {
                    locale: stripeLocale,
                });
            }
            setStripePromise(stripePromise);
        };

        if (transaction && transaction.paymentIntent) {
            fetchStripeDate();
        }
    }, [transaction, locale]);

    // todo remove when block fee is implemented
    useEffect(() => {
        if (
            transaction?._id &&
            transaction?.pricing?.pricingOption === EStationPricingOption.free
        ) {
            handleOpenChargePage(transaction._id);
        }
    }, [transaction]);

    const isLoading = transactionLoading && !transaction;
    const shouldRedirectHome = !transactionLoading && isNotFoundError(error);

    const connector = transaction?.station?.connectors?.[transaction.connectorId - 1] ?? undefined;
    const connectorType = connector?.type;

    const connectorLabel = useMemo(() => {
        if (!connector || !displayStation || transaction?.connectorId == null) {
            return undefined;
        }

        const connectorIndex = transaction.connectorId - 1;
        const connectorsArray = displayStation.connectors ?? [];
        const connectorNumber = `${displayStation.stationPosition ?? ''}${
            connectorsArray.length > 1 ? `${getLetterByIndex(connectorIndex)}` : ''
        }`;

        return getConnectorDisplayLabel({
            connectorNumber,
            shortCode: connector.qrCode?.shortCode,
            connectorIdentifier: displayStation.stationLocation?.connectorIdentifier,
        });
    }, [connector, displayStation, transaction?.connectorId]);

    return (
        <Modal
            sx={{
                '& 	.MuiModal-backdrop': {
                    backgroundColor: isDesktop ? '#15457580' : '#ffffff',
                    backdropFilter: 'blur(50px)',
                    overflowY: 'auto',
                },
                '&': {
                    zIndex: 10001,
                },
            }}
            open={true}
            slotProps={{
                backdrop: {
                    timeout: 300,
                },
            }}
            closeAfterTransition
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description">
            <Fade appear in>
                <div css={payStyles.modalContainer}>
                    <MenuBlock station={displayStation} style={{ zIndex: Z_INDEX.PAY_MENU_BLOCK }} />
                    <AnimationWrapper>
                        <div css={payStyles.modalContentBlock}>
                            {isLoading || shouldRedirectHome ? (
                                <div css={payStyles.spinnerContainer}>
                                    <SpinnerLoader />
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <>
                                            {transaction?._id && (
                                                <SavedContactsPreview
                                                    phone={transaction?.phone}
                                                    email={transaction?.email}
                                                    transactionId={transaction?._id}
                                                />
                                            )}
                                            {connector && connectorType ? (
                                                <>
                                                    <div css={payStyles.infoWrapper}>
                                                        <div css={payStyles.info}>
                                                            <div css={payStyles.infoBlock}>
                                                                <div css={payStyles.infoBlockTitle}>
                                                                    {intl.formatMessage({
                                                                        id: 'connector',
                                                                    })}
                                                                </div>
                                                                <div
                                                                    css={[
                                                                        payStyles.infoBlockValue,
                                                                        payStyles.connectorId,
                                                                    ]}>
                                                                    {connectorLabel}
                                                                    {` - `}
                                                                    {connectorType}
                                                                </div>
                                                            </div>
                                                            <div css={payStyles.infoBlock}>
                                                                <div css={payStyles.infoBlockTitle}>
                                                                    {intl.formatMessage({
                                                                        id: 'toPay',
                                                                    })}
                                                                </div>
                                                                <div css={payStyles.infoBlockValue}>
                                                                    {priceTotal}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <p css={payStyles.description}>
                                                        {intl.formatMessage(
                                                            { id: 'stripe.description' },
                                                            {
                                                                0: (
                                                                    <Link
                                                                        key="terms-of-offer"
                                                                        css={payStyles.termsLink}
                                                                        to={'/terms-of-offer'}
                                                                        target={'_blank'}
                                                                        rel="noreferrer">
                                                                        {intl.formatMessage({
                                                                            id: 'termsOfOffer.title',
                                                                        })}
                                                                    </Link>
                                                                ),
                                                            }
                                                        )}
                                                    </p>
                                                </>
                                            ) : (
                                                <div css={payStyles.spinnerContainer}>
                                                    <SpinnerLoader />
                                                </div>
                                            )}
                                        </>
                                        <>
                                            {stripePromise &&
                                            transaction?.paymentIntent?.client_secret ? (
                                                <Elements
                                                    key={`stripe-elements-${locale}`}
                                                    stripe={stripePromise}
                                                    options={{
                                                        locale: getStripeLocale(locale),
                                                        clientSecret:
                                                            transaction.paymentIntent
                                                                ?.client_secret || '',
                                                    }}>
                                                    <StripePaymentForm
                                                        paymentIntent={transaction.paymentIntent}
                                                        onSuccess={() =>
                                                            handleOpenChargePage(transaction?._id)
                                                        }
                                                        guestId={transaction._id}
                                                        priceTotal={priceTotal}
                                                    />
                                                </Elements>
                                            ) : null}
                                        </>
                                    </div>
                                    <div css={payStyles.modalContentBlockFooter}>
                                        <PoweredBy />
                                    </div>
                                </>
                            )}
                        </div>
                    </AnimationWrapper>
                </div>
            </Fade>
        </Modal>
    );
};

export default Pay;
